import React from 'react'
import { RefSelectProps, Select, SelectProps } from 'antd'
import { DefaultOptionType } from 'antd/es/select'
import { useTranslation } from 'react-i18next'
import { useScenarios } from '../../queries/useScenarios'
import { BudgetingScenario } from '../../types/BudgetingScenario'
import { defaultOptionsSelector, groupedOptionsSelector } from '../../selectors/selectors'
import { ReportDataType } from '../../../../redux/context/reports/types'
import ScenarioPlaceholder from './ScenarioPlaceholder'

interface ScenarioSelectProps extends SelectProps {
  companyId: string
  dataType?: ReportDataType.budget | ReportDataType.forecast
  optionSelector?: (data: BudgetingScenario[]) => DefaultOptionType[]
}

const ScenarioSelect = React.forwardRef<RefSelectProps, ScenarioSelectProps>(
  ({ companyId, dataType, optionSelector, ...selectProps }, ref) => {
    const { t } = useTranslation()

    // Use grouped selector when dataType is 'all' (undefined), otherwise use default or custom selector
    const defaultSelector = dataType === undefined ? groupedOptionsSelector : defaultOptionsSelector

    const { data: scenarioOptions, isLoading } = useScenarios({
      companyId,
      select: optionSelector || defaultSelector,
      dataType
    })

    const { labelRender: customLabelRender, ...restSelectProps } = selectProps

    const labelRender: SelectProps['labelRender'] = props => {
      if (isLoading) return `${t('global:loading')}...`
      if (customLabelRender) return customLabelRender(props)
      return props.label ?? t('global:deleted')
    }

    return (
      <Select
        ref={ref}
        labelRender={labelRender}
        loading={isLoading}
        showSearch
        placeholder={<ScenarioPlaceholder dataType={dataType} />}
        style={{ width: '100%', float: 'left' }}
        filterOption={(input, option) => (option?.label?.toString() ?? '').toLowerCase().includes(input.toLowerCase())}
        options={scenarioOptions}
        {...restSelectProps}
      />
    )
  }
)

ScenarioSelect.displayName = 'ScenarioSelect'

export default ScenarioSelect
